import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useRouter } from "next/router";

import { Check } from "@chef/icons/large";
import { Card, Skeleton } from "@chef/components";
import { AppShell } from "@chef/feature-app-shell";
import {
  BRAND_PATHS,
  CURRENCY,
  getDishCount,
  ORDER_TYPE_IDS,
  PAYMENT_PARTNER_IDS,
} from "@chef/constants";
import {
  resetSignupState,
  selectSignupAffiliateIds,
  selectSignupDeliveryData,
  selectSignupGiftcard,
  useBillingQuery,
  useCalendarQuery,
} from "@chef/state-management";
import {
  formatDate,
  formatTimeInterval,
  getWeek,
  getYear,
  isMealboxBasketType,
  isMealboxProduct,
  nextDeliveryWeekDateObj,
} from "@chef/helpers";

import { fromØre } from "@chef/utils/currency";
import { useEffectOnce } from "@chef/hooks";
import {
  STAGE,
  useAppSelector,
  usePreselector,
  useTrack,
} from "@chef/state-management/hooks";
import { getAttribute } from "@chef/state-management/helpers";
import { CompletedWithAllergenWarning } from "@chef/smart-components";
import { isEqualStrings } from "@chef/utils/equal";

import { useTrackingData } from "../hooks/useTrackingData";
import {
  OnSatisfactionFeedbackSubmitted,
  SatisfactionFeedback,
} from "../components/SatisfactionFeedback";
import { useSignupSession } from "../hooks/useSignupSession";

import { intl } from "./SignupFinished.Intl";

export const SignupFinished = () => {
  const dispatch = useDispatch();
  const nextDelivery = nextDeliveryWeekDateObj();
  const router = useRouter();

  const trackingData = useTrackingData();
  const giftCard = useSelector(selectSignupGiftcard);
  const deliveryData = useSelector(selectSignupDeliveryData);
  const affiliateIds = useSelector(selectSignupAffiliateIds);
  const couponDetails = useAppSelector((state) => state.signupRtk.coupon);

  const { data: billingData } = useBillingQuery();

  const { expire } = usePreselector({
    onStageChange: {
      [STAGE.COMPLETED]: () => {
        expire();
      },
      [STAGE.COMPLETED_WITH_ALLERGEN]: () => {
        expire();
      },
    },
  });

  const { signupSession } = useSignupSession();

  const track = useTrack();

  const week = getWeek(nextDelivery);
  const year = getYear(nextDelivery);

  const { data, isLoading: isLoadingCalendar } = useCalendarQuery({
    week,
    year,
    range: 8,
  });

  const [showLoading, setShowLoading] = useState(false);

  const product = billingData?.billing.baskets
    .flatMap((b) => b.basketProducts)
    .find((b) => isMealboxProduct(b.variation.product));

  const handleSatisfactionSubmitted: OnSatisfactionFeedbackSubmitted = ({
    comment,
    value,
  }) => {
    setShowLoading(true);

    if (value) {
      {
        track("signupFeedbackSubmitted", {
          affiliation: "Frontend process",
          satisfaction: value,
          details: comment,
          giftcard_code: giftCard?.code || "",
          post_code: deliveryData?.postalCode,
          payment_type:
            billingData?.billing.paymentPartnerId ===
            PAYMENT_PARTNER_IDS.COLLECTOR
              ? "invoice"
              : "cc",

          products: trackingData.products,
        });
      }
    }

    router.push(BRAND_PATHS.MY_MENU_PAGE);
  };

  useEffect(() => {
    return () => {
      dispatch(resetSignupState());
    };
  }, []);

  useEffectOnce(() => {
    let paymentMethod: "vipps" | "cc" | "invoice" = "cc";

    if (
      billingData?.billing.paymentPartnerId === PAYMENT_PARTNER_IDS.COLLECTOR
    ) {
      paymentMethod = "invoice";
    }

    if (
      billingData?.billing.paymentPartnerId ===
      PAYMENT_PARTNER_IDS.VIPPS_MOBILEPAY
    ) {
      paymentMethod = "vipps";
    }

    const deliveryFee = fromØre(signupSession?.basket?._meta?.deliveryFee || 0);
    const total = fromØre(signupSession?.basket?._meta?.net || 0);

    const products = trackingData.products?.map((p) => {
      if (product && isEqualStrings(p.variant_id, product?.variationId)) {
        const slv = getAttribute("static_lifetime_value", product.variation);

        if (slv) {
          return {
            ...p,
            static_lifetime_value: +slv,
          };
        }
      }

      return p;
    });

    track("Order Completed", {
      affiliates: [
        {
          name: "adtraction",
          transaction_id: affiliateIds.at,
        },
      ],
      affiliation: "Frontend process",
      checkout_id: signupSession?.id || "",
      coupon: trackingData.coupon,
      currency: CURRENCY,
      campaign_id: trackingData.campaign_id,
      discount: trackingData.discount,
      discount_code: couponDetails?.name,
      discount_code_id: couponDetails?.discountId,
      order_id: billingData?.billing.agreementId?.toString() || "",
      order_type: "Recurring",
      order_type_id: ORDER_TYPE_IDS.RECURRING,
      payment_method: paymentMethod,
      products,
      shipping: deliveryFee,
      total,
    });
  }, !!(signupSession && trackingData.products?.some((p) => p.variant_id)));

  let firstWeekDeliveryInformationString = "";

  try {
    const firstDeliveryWeek = data?.calendar.find(
      (w) =>
        !w.isBeforeStartDateWeek &&
        w.status === 10 &&
        w.baskets.some((b) => b.shippingDetails.hasDelivery),
    );

    const firstWeekDeliveryInformation = firstDeliveryWeek?.baskets.find(
      (b) => b.shippingDetails.hasDelivery && isMealboxBasketType(b),
    )?.shippingDetails.plannedDeliveryInformation;

    firstWeekDeliveryInformationString = `${formatDate(
      firstWeekDeliveryInformation?.deliveryDate || 0,
      undefined,
      { lowerCase: true },
    )} kl. ${formatTimeInterval(
      firstWeekDeliveryInformation?.from,
      firstWeekDeliveryInformation?.to,
      "m",
    )}`;
  } catch (e) {
    console.error(e);
    firstWeekDeliveryInformationString = "unknown date"; // TODO: What do we do here?
  }

  const portions = product && getAttribute("Portions", product.variation);

  const generatedDishCount = getDishCount({
    week,
    year,
    portions: portions ? +portions : 4,
  });

  return (
    <AppShell title={intl.THANKS_FOR_YOUR_ORDER}>
      <AppShell.Meta />

      <AppShell.Main grid>
        <Card
          className="flex flex-col items-center col-span-4 px-4 py-8 -mt-6 md:col-span-6 md:col-start-2 xl:col-start-4 lg:p-20"
          noPadding
        >
          <div className="flex items-center justify-center w-20 h-20 rounded-full bg-grey-3">
            <Check className="mt-3 mb-3" width={20} height={20} />
          </div>

          <h1 className="mt-4 text-lg">
            <strong>{intl.THANKS_FOR_YOUR_ORDER}</strong>
          </h1>

          <CompletedWithAllergenWarning className="mt-4 mb-2" />

          <p className="mt-2 text-center">
            {intl.YOUR_FIRST_DELIVERY_ARRIVES}{" "}
            {isLoadingCalendar && (
              <Skeleton
                as="span"
                width={64}
                height={4}
                className="inline-block mx-1 w-52"
              />
            )}
            {!isLoadingCalendar && (
              <strong>{firstWeekDeliveryInformationString}. </strong>
            )}
            {intl.SEE_YOUR_MENU_AND_CHOOSE_FREELY_BETWEEN_N_DISHES(
              generatedDishCount,
            )}
          </p>

          <SatisfactionFeedback
            onSubmit={handleSatisfactionSubmitted}
            loading={showLoading}
          />
        </Card>
      </AppShell.Main>
    </AppShell>
  );
};

export async function getStaticProps() {
  return {
    props: {
      showFooter: false,
      shouldRenderHeader: false,
      shouldRenderFooter: false,
    },
  };
}
